// Map a raw upstream provider error → a stable LlmRuntimeErrorKind.
//
// Strategy (most → least reliable): provider structured code FIRST, HTTP status
// SECOND, free-text message regex THIRD. `matchedPattern` records which rule
// fired so production drift in vendor wording is observable (and the pattern
// table can be tuned). Fallthrough is `unknown` — surfaced explicitly, never a
// silent guess (feedback_no_fallbacks).
//
// The auth-revoked vs auth-refreshable vs provider-unavailable patterns are the
// SAME signatures used by lx-llm-auth's classifyOAuthRefreshFailure; the two are
// kept in sync by a shared incident-string test fixture in each package (this
// package can't import lx-llm-auth — it must stay browser-safe + dep-free).
import { LLM_RUNTIME_ERROR_KINDS } from "./taxonomy.js";
// Ordered most-specific → least-specific. The broad transient bucket
// (provider-unavailable) is near the end so specific auth/billing/model/context
// signatures win first.
const MESSAGE_RULES = [
    {
        kind: "auth-revoked",
        // Kept in lock-step with lx-llm-auth's REVOKED_RE (F0.2 — pinned by the
        // shared incident-string fixture). `invalid_grant` is the marker the
        // POST-Fix-0 enriched refresh error carries; without it the client would
        // still show the generic card for the real incident.
        id: "regex:auth-revoked",
        re: /token_revoked|invalidated[ _-]?oauth[ _-]?token|refresh_token_invalidated|refresh_token_reused|revoked_access_token|token[ _-]?revoked|invalid_grant|invalid[ _-]?refresh[ _-]?token|expired or revoked|access_denied|sign(?:ing)? in again/i,
    },
    {
        kind: "auth-refreshable",
        id: "regex:auth-refreshable",
        re: /token.{0,6}expired|expired_token|access[ _-]?token[ _-]?expired/i,
    },
    {
        kind: "auth-missing",
        id: "regex:auth-missing",
        re: /no api key provided|missing authorization|no credential|not configured for your org/i,
    },
    {
        kind: "quota-exhausted",
        id: "regex:quota-exhausted",
        re: /usage_limit_reached|usage_not_included|quota_exceeded|insufficient_quota|billing_hard_limit|exceeded your current quota/i,
    },
    {
        kind: "context-length",
        id: "regex:context-length",
        // ⚠ The SIZE vocabulary is not decorative. OpenClaw's own in-loop overflow abort says
        // "Context overflow: estimated context size exceeds safe threshold during tool loop"
        // — "context SIZE", never "context length" — so the length-only pattern missed the one
        // failure this kind exists to name, and the affordance never engaged. Measured on two
        // live specimens (2026-08-14, diagIds ovf-mssnzkxv-hEEPuA + ovf-mssow20e-7qHaqQ); the
        // first is pinned VERBATIM in the tests. Provider wording and host wording are separate
        // dialects for the same condition, and this rule has to speak both.
        re: /context[ _-]?(?:length|size|overflow)|maximum context|too many tokens|prompt is too long|context_length_exceeded|reduce the length/i,
    },
    {
        kind: "content-rejected",
        id: "regex:content-rejected",
        re: /content_policy_violation|unsafe_content|safety[ _-]?violation|content[ _-]?filter|flagged as|refused to/i,
    },
    {
        kind: "model-unknown",
        id: "regex:model-unknown",
        re: /model[ _-].{0,16}(not found|does not exist|not supported|unknown)|invalid_model|model_not_found|no such model/i,
    },
    {
        kind: "rate-limit",
        id: "regex:rate-limit",
        re: /rate.?limit|too many requests|429/i,
    },
    {
        kind: "provider-unavailable",
        id: "regex:provider-unavailable",
        re: /internal server error|bad gateway|gateway timeout|service unavailable|overloaded|temporarily unavailable|time(?:d)?\s?out|econnreset|econnrefused|enotfound|etimedout|eai_again|fetch failed|socket hang up|\b50[0-24]\b|\b529\b/i,
    },
];
function messageOf(err) {
    if (typeof err === "string")
        return err;
    if (err && typeof err === "object" && "message" in err) {
        const m = err.message;
        if (typeof m === "string")
            return m;
    }
    return String(err);
}
/** Provider structured code → kind (the most reliable signal when present). */
const CODE_KIND = {
    token_revoked: "auth-revoked",
    refresh_token_invalidated: "auth-revoked",
    refresh_token_reused: "auth-revoked",
    invalid_grant: "auth-revoked",
    token_expired: "auth-refreshable",
    insufficient_quota: "quota-exhausted",
    billing_hard_limit_reached: "quota-exhausted",
    rate_limit_exceeded: "rate-limit",
    context_length_exceeded: "context-length",
    content_policy_violation: "content-rejected",
    model_not_found: "model-unknown",
};
export function classifyProviderError(err, ctx = {}) {
    const provider = ctx.provider;
    const base = { provider, retryAfterSeconds: ctx.retryAfterSeconds };
    // 1. Provider structured code first.
    if (ctx.providerCode) {
        const kind = CODE_KIND[ctx.providerCode.toLowerCase()];
        if (kind)
            return { ...base, kind, matchedPattern: `code:${ctx.providerCode}` };
    }
    // 2. HTTP status (only the unambiguous ones; bare 401 is NOT guessed).
    if (ctx.status === 402)
        return { ...base, kind: "quota-exhausted", matchedPattern: "status:402" };
    if (ctx.status === 429)
        return { ...base, kind: "rate-limit", matchedPattern: "status:429" };
    if (ctx.status === 408) {
        return { ...base, kind: "provider-unavailable", matchedPattern: "status:408" };
    }
    if (ctx.status && ctx.status >= 500 && ctx.status <= 504) {
        return { ...base, kind: "provider-unavailable", matchedPattern: `status:${ctx.status}` };
    }
    if (ctx.status === 529) {
        return { ...base, kind: "provider-unavailable", matchedPattern: "status:529" };
    }
    // 3. Free-text message regex.
    const message = messageOf(err);
    for (const rule of MESSAGE_RULES) {
        if (rule.re.test(message))
            return { ...base, kind: rule.kind, matchedPattern: rule.id };
    }
    return { ...base, kind: "unknown", matchedPattern: "fallthrough" };
}
/** Guard for runtime values arriving over the wire from an older runtime. */
export function isLlmRuntimeErrorKind(v) {
    return typeof v === "string" && LLM_RUNTIME_ERROR_KINDS.includes(v);
}
//# sourceMappingURL=classify.js.map